/**
 * Card flights (FLIP). Every visible card carries data-card-id (cards are
 * unique per copy), so after each state change we compare where each card is
 * now with where it was, and animate from the old spot. Cards that appear
 * from somewhere invisible (the deck, a face-down slot) take their origin
 * from the engine events. Reduced motion: no flights.
 */
import type { GameEvent } from '../../engine';

export type Rects = Map<string, DOMRect>;

const running = new Set<Animation>();

/** Finish every running flight so measurements see final layout. */
export function settleFlights(): void {
  for (const a of running) {
    try {
      a.finish();
    } catch {
      /* already gone */
    }
  }
  running.clear();
}

export function captureRects(root: HTMLElement | null): Rects {
  const out: Rects = new Map();
  if (!root) return out;
  root.querySelectorAll<HTMLElement>('[data-card-id]').forEach((el) => {
    out.set(`card:${el.dataset.cardId}`, el.getBoundingClientRect());
  });
  root.querySelectorAll<HTMLElement>('[data-slot-key]').forEach((el) => {
    out.set(`slot:${el.dataset.slotKey}`, el.getBoundingClientRect());
  });
  root.querySelectorAll<HTMLElement>('[data-pile]').forEach((el) => {
    out.set(`pile:${el.dataset.pile}`, el.getBoundingClientRect());
  });
  return out;
}

/** Where a newly visible card came from, according to the events. */
export function originFor(cardId: string, events: GameEvent[]): string | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.type === 'drewDeck' && e.card?.id === cardId) return 'pile:deck';
    if (e.type === 'swapped' && e.old.id === cardId && !e.oldWasFaceUp) return `slot:${e.player}:${e.slot}`;
    if (e.type === 'swapped' && e.card.id === cardId) return 'pile:held';
    if (e.type === 'discarded' && e.card.id === cardId) return 'pile:held';
  }
  return null;
}

export function playFlights(root: HTMLElement | null, before: Rects, after: Rects, events: GameEvent[], reduced: boolean): number {
  if (!root || reduced || before.size === 0) return 0;
  let count = 0;
  root.querySelectorAll<HTMLElement>('[data-card-id]').forEach((el) => {
    const id = el.dataset.cardId!;
    const to = after.get(`card:${id}`);
    if (!to || to.width === 0) return;
    let from = before.get(`card:${id}`);
    if (!from) {
      const origin = originFor(id, events);
      if (origin) from = before.get(origin);
    }
    if (!from || from.width === 0) return;
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    const sx = from.width / to.width;
    const sy = from.height / to.height;
    if (Math.abs(dx) < 2 && Math.abs(dy) < 2 && Math.abs(sx - 1) < 0.02) return;
    const dist = Math.hypot(dx, dy);
    const duration = Math.min(560, 300 + dist * 0.35);
    const prevZ = el.style.zIndex;
    el.style.zIndex = '60';
    el.style.pointerEvents = 'none';
    const anim = el.animate(
      [
        { transformOrigin: 'top left', transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy}) rotate(${dx > 0 ? -4 : 4}deg)` },
        { transformOrigin: 'top left', transform: 'none' },
      ],
      { duration, easing: 'cubic-bezier(.2,.8,.2,1)' },
    );
    running.add(anim);
    const done = () => {
      running.delete(anim);
      el.style.zIndex = prevZ;
      el.style.pointerEvents = '';
    };
    anim.onfinish = done;
    anim.oncancel = done;
    count++;
  });
  return count;
}
